import { useRef, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import ObjectActionCreators from '../../../actions';
import { usePanelsConfig } from '../NavigTree/usePanelsConfig';
import { ExpandColumnIcon } from '../../../constants/icon';

const MIN_DELTA_PX = 4; // seuil minimal avant update
const SNAP_STEP_PX = 5; // pas d’accroche (mettre à 1 pour désactiver)

const TreePaneResizer = ({ panelNumber, width, level }) => {
  const resizerRef = useRef(null);
  const dispatch = useDispatch();
  const { updatePanelConfig } = usePanelsConfig();

  const startXRef = useRef(null);
  const lastAppliedXRef = useRef(null);
  const lastXRef = useRef(null);
  const rafIdRef = useRef(0);

  const applyWithThresholdAndSnap = useCallback(
    (clientX) => {
      if (lastAppliedXRef.current == null) {
        lastAppliedXRef.current = clientX;
      }
      const delta = clientX - lastAppliedXRef.current;

      // 1) Dead zone
      if (Math.abs(delta) < MIN_DELTA_PX) return;

      // 2) Snap (on arrondit la cible à un pas fixe)
      let targetX = clientX;
      if (SNAP_STEP_PX > 1) {
        targetX = Math.round(clientX / SNAP_STEP_PX) * SNAP_STEP_PX;
      }

      lastAppliedXRef.current = targetX;

      // Ton update (level + 1 conservé)
      updatePanelConfig(level + 1, targetX);
    },
    [level, updatePanelConfig]
  );

  const flushRaf = useCallback(() => {
    rafIdRef.current = 0;
    if (lastXRef.current == null) return;
    applyWithThresholdAndSnap(lastXRef.current);
  }, [applyWithThresholdAndSnap]);

  const scheduleRaf = useCallback(() => {
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(flushRaf);
  }, [flushRaf]);

  const handleMouseMove = (e) => {
    if (e.buttons !== 1) {
      handleMouseUp();
      return;
    }
    lastXRef.current = e.clientX;
    scheduleRaf();
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    startXRef.current = e.clientX;
    lastAppliedXRef.current = e.clientX; // point de référence initial
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }
    // flush final (si besoin)
    if (lastXRef.current != null) {
      applyWithThresholdAndSnap(lastXRef.current);
    }
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
    };
  }, []);

  return (
    <div
      ref={resizerRef}
      className="resizer"
      onMouseDown={handleMouseDown}
      draggable="false"
      style={{
        zIndex: 70000,
        width: '33px',
        backgroundColor: 'var(--ds-bg-base)',
        position: 'absolute',
        cursor: 'col-resize',
        height: '20px',
        border: '0px solid red',
        borderRadius: '0 0 50% 50%',
        top: '0px',
        left: `var(--resizer-${level}-posx, 0px)`,
        display: 'flex',
        alignItems: 'center', // vertical
        justifyContent: 'center', // horizontal
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      }}
    >
      <ExpandColumnIcon fill="var(--ds-base)" />
    </div>
  );
};

export default TreePaneResizer;
